import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiEdit, FiTrash2, FiSend, FiCheck, FiX, FiCheckCircle } from 'react-icons/fi';
import {
  getChangeRequest,
  deleteChangeRequest,
  submitChangeRequest,
  reviewChangeRequest,
  approveChangeRequest,
  rejectChangeRequest,
  implementChangeRequest,
  cancelChangeRequest,
  getStatusBadgeClass,
  getPriorityBadgeClass,
  getImpactLevelBadgeClass,
  formatDate,
  canEdit,
  canSubmit,
  canReview,
  canApprove,
  canReject,
  canImplement,
  canCancel,
  canDelete
} from '../../services/changeRequestService';

const ChangeRequestView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [changeRequest, setChangeRequest] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchChangeRequest();
  }, [id]);

  const fetchChangeRequest = async () => {
    try {
      setLoading(true);
      const response = await getChangeRequest(id);
      setChangeRequest(response.data);
    } catch (error) {
      console.error('Error fetching change request:', error);
      toast.error('Failed to load change request');
      navigate('/app/change-requests');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (actionFn, actionName) => {
    if (!window.confirm(`Are you sure you want to ${actionName} this change request?`)) {
      return;
    }

    try {
      setActionLoading(true);
      await actionFn(id);
      toast.success(`Change request ${actionName} successfully`);
      fetchChangeRequest();
    } catch (error) {
      console.error(`Error ${actionName} change request:`, error);
      toast.error(error.response?.data?.message || `Failed to ${actionName} change request`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this change request? This action cannot be undone.')) {
      return;
    }

    try {
      setActionLoading(true);
      await deleteChangeRequest(id);
      toast.success('Change request deleted successfully');
      navigate('/app/change-requests');
    } catch (error) {
      console.error('Error deleting change request:', error);
      toast.error(error.response?.data?.message || 'Failed to delete change request');
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-2 text-gray-600">Loading...</p>
      </div>
    );
  }

  if (!changeRequest) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-600">Change request not found</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/app/change-requests')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <FiArrowLeft className="mr-2" /> Back to Change Requests
        </button>
        
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{changeRequest.title}</h1>
            <div className="flex gap-2 mt-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(changeRequest.status)}`}>
                {changeRequest.status}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityBadgeClass(changeRequest.priority)}`}>
                {changeRequest.priority}
              </span>
              <span className="px-3 py-1 rounded-full text-sm font-medium badge badge-info">
                {changeRequest.change_type}
              </span>
            </div>
          </div>

          <div className="flex gap-2">
            {canEdit(changeRequest) && (
              <button
                onClick={() => navigate(`/app/change-requests/${id}/edit`)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                disabled={actionLoading}
              >
                <FiEdit /> Edit
              </button>
            )}
            {canDelete(changeRequest) && (
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                disabled={actionLoading}
              >
                <FiTrash2 /> Delete
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Workflow Actions */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions</h2>
        <div className="flex flex-wrap gap-3">
          {canSubmit(changeRequest) && (
            <button
              onClick={() => handleAction(submitChangeRequest, 'submit')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              disabled={actionLoading}
            >
              <FiSend /> Submit for Review
            </button>
          )}
          {canReview(changeRequest) && (
            <button
              onClick={() => handleAction(reviewChangeRequest, 'review')}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center gap-2"
              disabled={actionLoading}
            >
              <FiCheck /> Mark as Under Review
            </button>
          )}
          {canApprove(changeRequest) && (
            <button
              onClick={() => handleAction(approveChangeRequest, 'approve')}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              disabled={actionLoading}
            >
              <FiCheckCircle /> Approve
            </button>
          )}
          {canReject(changeRequest) && (
            <button
              onClick={() => handleAction(rejectChangeRequest, 'reject')}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
              disabled={actionLoading}
            >
              <FiX /> Reject
            </button>
          )}
          {canImplement(changeRequest) && (
            <button
              onClick={() => handleAction(implementChangeRequest, 'implement')}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
              disabled={actionLoading}
            >
              <FiCheckCircle /> Mark as Implemented
            </button>
          )}
          {canCancel(changeRequest) && (
            <button
              onClick={() => handleAction(cancelChangeRequest, 'cancel')}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
              disabled={actionLoading}
            >
              <FiX /> Cancel
            </button>
          )}
        </div>
      </div>

      {/* Basic Information */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-500">Project</label>
            <p className="mt-1 text-gray-900">{changeRequest.project?.name || 'N/A'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Requested By</label>
            <p className="mt-1 text-gray-900">
              {changeRequest.requester ? `${changeRequest.requester.first_name} ${changeRequest.requester.last_name}` : 'N/A'}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Requested Date</label>
            <p className="mt-1 text-gray-900">{formatDate(changeRequest.requested_date)}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Target Completion</label>
            <p className="mt-1 text-gray-900">{formatDate(changeRequest.target_completion_date)}</p>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Description</h2>
        <p className="text-gray-700 whitespace-pre-wrap">{changeRequest.description}</p>
      </div>

      {/* Justification & Benefits */}
      {(changeRequest.justification || changeRequest.benefits) && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Justification & Benefits</h2>
          {changeRequest.justification && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-500 mb-2">Justification</label>
              <p className="text-gray-700 whitespace-pre-wrap">{changeRequest.justification}</p>
            </div>
          )}
          {changeRequest.benefits && (
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-2">Benefits</label>
              <p className="text-gray-700 whitespace-pre-wrap">{changeRequest.benefits}</p>
            </div>
          )}
        </div>
      )}

      {/* Impact Analysis */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Impact Analysis</h2>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-500 mb-2">Overall Impact Level</label>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getImpactLevelBadgeClass(changeRequest.impact_level)}`}>
            {changeRequest.impact_level}
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {changeRequest.impact_scope && (
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-2">Scope Impact</label>
              <p className="text-gray-700">{changeRequest.impact_scope}</p>
            </div>
          )}
          {changeRequest.impact_schedule && (
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-2">Schedule Impact</label>
              <p className="text-gray-700">{changeRequest.impact_schedule}</p>
            </div>
          )}
          {changeRequest.impact_budget && (
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-2">Budget Impact</label>
              <p className="text-gray-700">{changeRequest.impact_budget}</p>
            </div>
          )}
        </div>
      </div>

      {/* Implementation Plan */}
      {changeRequest.implementation_plan && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Implementation Plan</h2>
          <p className="text-gray-700 whitespace-pre-wrap">{changeRequest.implementation_plan}</p>
        </div>
      )}

      {/* Workflow Information */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Workflow Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {changeRequest.submitted_date && (
            <div>
              <label className="block text-sm font-medium text-gray-500">Submitted Date</label>
              <p className="mt-1 text-gray-900">{formatDate(changeRequest.submitted_date)}</p>
            </div>
          )}
          {changeRequest.reviewed_date && (
            <div>
              <label className="block text-sm font-medium text-gray-500">Reviewed Date</label>
              <p className="mt-1 text-gray-900">{formatDate(changeRequest.reviewed_date)}</p>
            </div>
          )}
          {changeRequest.approved_date && (
            <div>
              <label className="block text-sm font-medium text-gray-500">Approved Date</label>
              <p className="mt-1 text-gray-900">{formatDate(changeRequest.approved_date)}</p>
            </div>
          )}
          {changeRequest.rejected_date && (
            <div>
              <label className="block text-sm font-medium text-gray-500">Rejected Date</label>
              <p className="mt-1 text-gray-900">{formatDate(changeRequest.rejected_date)}</p>
            </div>
          )}
          {changeRequest.implemented_date && (
            <div>
              <label className="block text-sm font-medium text-gray-500">Implemented Date</label>
              <p className="mt-1 text-gray-900">{formatDate(changeRequest.implemented_date)}</p>
            </div>
          )}
          {changeRequest.rejection_reason && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-500">Rejection Reason</label>
              <p className="mt-1 text-gray-900">{changeRequest.rejection_reason}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangeRequestView;


